// src/pages/ReferralLinks.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Row, Col, Card, Table, Button,
    Spinner, Alert, Badge, Modal, Form, InputGroup
} from 'react-bootstrap';
import {
    PlusCircle, Clipboard, PencilSquare,
    TrashFill, CheckCircleFill, XCircleFill
} from 'react-bootstrap-icons';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

const ReferralLinks = () => {
    const [referralLinks, setReferralLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deleting, setDeleting] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [linkToDelete, setLinkToDelete] = useState(null);
    const [copied, setCopied] = useState(null);

    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        fetchReferralLinks();
    }, []);

    const fetchReferralLinks = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/seller/referral-links');
            setReferralLinks(response.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || t('errors.loadingError'));
            console.error('Error fetching referral links:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = (code) => {
        const url = `${window.location.origin}/register?ref=${code}`;
        navigator.clipboard.writeText(url)
            .then(() => {
                setCopied(code);
                setTimeout(() => setCopied(null), 2000);
            })
            .catch(err => {
                console.error('Could not copy text: ', err);
            });
    };

    const handleDelete = (link) => {
        setLinkToDelete(link);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!linkToDelete) return;

        setDeleting(linkToDelete._id);
        setShowDeleteModal(false);

        try {
            await api.delete(`/api/seller/referral-links/${linkToDelete._id}`);
            setReferralLinks(referralLinks.filter(l => l._id !== linkToDelete._id));
        } catch (err) {
            setError(err.response?.data?.message || t('errors.deleteError'));
            console.error('Error deleting referral link:', err);
        } finally {
            setDeleting(null);
            setLinkToDelete(null);
        }
    };

    const handleEdit = (id) => {
        navigate(`/referrals/links/${id}/edit`);
    };

    const handleCreate = () => {
        navigate('/referrals/links/new');
    };

    return (
        <Container className="my-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>{t('referral.myLinks')}</h1>
                {referralLinks.length < 100 && (
                    <Button
                        variant="primary"
                        onClick={handleCreate}
                        className="btn-highlight"
                    >
                        <PlusCircle className="me-2" />
                        {t('referral.createLink')}
                    </Button>
                )}
            </div>

            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            <Card className="glass-card">
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">{t('common.loading')}</p>
                        </div>
                    ) : referralLinks.length > 0 ? (
                        <div className="table-responsive">
                            <Table hover className="web3-table">
                                <thead>
                                <tr>
                                    <th>{t('common.name')}</th>
                                    <th>{t('referral.code')}</th>
                                    <th>{t('flightSheet.poolSettings')}</th>
                                    <th>{t('referral.referrals')}</th>
                                    <th>{t('common.status')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {referralLinks.map(link => (
                                    <tr key={link._id}>
                                        <td><strong>{link.name}</strong></td>
                                        <td>
                                            <code>{link.code}</code>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                onClick={() => handleCopyLink(link.code)}
                                                className="ms-2 p-0"
                                            >
                                                {copied === link.code ? (
                                                    <CheckCircleFill className="text-success" />
                                                ) : (
                                                    <Clipboard />
                                                )}
                                            </Button>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column">
                                                <span>{link.poolHost}:{link.poolPort}</span>
                                                <small className="text-muted">
                                                    {link.poolWorker}
                                                </small>
                                            </div>
                                        </td>
                                        <td>{link.referredUsersCount || 0}</td>
                                        <td>
                                            {link.active ? (
                                                <Badge bg="success" pill>{t('common.active')}</Badge>
                                            ) : (
                                                <Badge bg="secondary" pill>{t('common.inactive')}</Badge>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleCopyLink(link.code)}
                                                    title={t('referral.copy')}
                                                >
                                                    <Clipboard />
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleEdit(link._id)}
                                                    title={t('common.edit')}
                                                >
                                                    <PencilSquare />
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(link)}
                                                    disabled={deleting === link._id}
                                                    title={t('common.delete')}
                                                >
                                                    {deleting === link._id ? (
                                                        <Spinner animation="border" size="sm" />
                                                    ) : (
                                                        <TrashFill />
                                                    )}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <p className="mb-4">{t('referral.noLinks')}</p>
                            <Button
                                variant="primary"
                                onClick={handleCreate}
                                className="btn-highlight"
                            >
                                <PlusCircle className="me-2" />
                                {t('referral.createFirstLink')}
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Модальное окно подтверждения удаления */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('referral.deleteConfirmTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {linkToDelete && linkToDelete.referredUsersCount > 0 ? (
                        <p>{t('referral.deleteWarning', { count: linkToDelete.referredUsersCount })}</p>
                    ) : (
                        <p>{t('referral.deleteConfirm')}</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="danger" onClick={confirmDelete}>
                        {t('common.delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ReferralLinks;